import { VenueRequestFile } from '@go-shared/types/groupBooking';
import React from 'react';

import { BubbleButton, BubbleFormGroup, BubbleInput, BubbleSelect, BubbleFileM } from '../BubbleUI';
import { FormGenericContainer } from '../FormGenericContainer';
import { confirmFormExit, GroupForm } from './Workflows';
import { BrandGroupType } from '@go-shared/types/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import Editor from '../TinyEditor';
import { getCommonTexts } from '@go-shared/lib/brand';
import { UPLOAD_RULES } from '@go-shared/lib/uploads';
import BubbleCollapsibleSection from '../BubbleUI/BubbleCollapsibleSection';
import { RadioButton, RadioButtonGroup } from '@spark-labs/avocado-ui';
import {
  GroupDetailsFieldName,
  isFieldValueEmpty,
  useGroupDetailsValidation,
} from '@go-client/hooks/useGroupDetailsValidation';

interface EnterGroupPageProps {
  groupForm: GroupForm;
  changeGroupForm: <T extends keyof GroupForm>(key: T, val: GroupForm[T]) => void;
  groupTypeOpts: BrandGroupType[];
  title?: string;
  pageIndex?: number;
  onClose?: () => void;
  onSubmitGroupDetails: () => void;
  onUpdateFile: (file: VenueRequestFile) => void;
  onUploadFile: (file: File) => void;
  onDeleteFile: (file: VenueRequestFile) => void;
}

export default function EnterGroupPage(props: EnterGroupPageProps) {
  const {
    mandatoryFields,
    touchedFields,
    invalidFields,
    formValid,
    handleFieldBlur,
    handleFieldValidityChange,
  } = useGroupDetailsValidation(props.groupForm);

  return (
    <div className="tflf-1">
      <FormGenericContainer
        className="h-screen flex flex-col"
        bodyClassName="flex-grow flex flex-col"
        footer={() => (
          <>
            <div className="text-slate-dark"></div>
            <BubbleButton
              className="ml-auto"
              variant="navy"
              disabled={!formValid}
              onClick={props.onSubmitGroupDetails}
            >
              Finish: Submit Group Details
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div className="mr-auto">
            <h1 className="text-lg text-slate-dark font-semibold">{props.title || 'Edit Group'}</h1>
            <h2 className="text-2xl text-navy">
              {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}Enter Group Details
            </h2>
          </div>
          <BubbleButton
            variant="navy"
            inverse
            onClick={() => confirmFormExit(props.groupForm._dirty, props.onClose)}
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <EnterGroupPageFields
          partyForm={props.groupForm}
          changePartyForm={props.changeGroupForm}
          groupTypeOpts={props.groupTypeOpts}
          onUpdateFile={props.onUpdateFile}
          onUploadFile={props.onUploadFile}
          onDeleteFile={props.onDeleteFile}
          mandatoryFields={mandatoryFields}
          touchedFields={touchedFields}
          invalidFields={invalidFields}
          onFieldBlur={handleFieldBlur}
          onFieldValidityChange={handleFieldValidityChange}
        />
      </FormGenericContainer>
    </div>
  );
}

interface EnterGroupPageFieldProps {
  partyForm: GroupForm;
  changePartyForm: <T extends keyof GroupForm>(key: T, val: GroupForm[T]) => void;
  groupTypeOpts: BrandGroupType[];
  className?: string;
  onUpdateFile?: (file: VenueRequestFile) => void;
  onUploadFile?: (file: File) => void;
  onDeleteFile?: (file: VenueRequestFile) => void;
  mandatoryFields?: Set<GroupDetailsFieldName>;
  touchedFields?: Set<GroupDetailsFieldName>;
  invalidFields?: Set<GroupDetailsFieldName>;
  onFieldBlur?: (fieldName: GroupDetailsFieldName) => void;
  onFieldValidityChange?: (fieldName: GroupDetailsFieldName, valid: boolean) => void;
}

export function EnterGroupPageFields(props: EnterGroupPageFieldProps) {
  const { brand } = useServerInfo();
  const mandatoryFields = props.mandatoryFields ?? new Set<GroupDetailsFieldName>(['name']);

  function fieldValidation(fieldName: GroupDetailsFieldName) {
    const mandatory = mandatoryFields.has(fieldName);
    const empty = isFieldValueEmpty(props.partyForm, fieldName);
    const touched = props.touchedFields?.has(fieldName) ?? false;
    const invalid = props.invalidFields?.has(fieldName) ?? false;
    let error: string | undefined;
    if (invalid) error = 'Invalid date';
    else if (touched && mandatory && empty) error = 'This field is required';
    return {
      asterisk: mandatory,
      error,
      onBlur: () => props.onFieldBlur?.(fieldName),
    };
  }

  // Native date inputs report an impossible date (e.g. 02/31) as an empty value, which BubbleInput
  // ignores, so the form would silently keep the previous valid date. Track the browser's
  // validity instead. Checked on blur too: going from empty to invalid doesn't fire onChange.
  function dateValidityHandlers(fieldName: GroupDetailsFieldName) {
    const report = (event: React.SyntheticEvent<HTMLInputElement>) =>
      props.onFieldValidityChange?.(fieldName, event.currentTarget.validity.valid);
    return { onChange: report, onBlur: report };
  }

  const {
    guest_experience,
    tour_leader,
    tour_leader_cabin,
    guests,
    group_contact_name,
    embark_date,
    debark_date,
    hospitality_desk,
    agency_contact_name,
    sales_dept_email,
    sales_dept_phone,
    party_name,
    group_id,
    group_type,
    cabin_drops,
    dining_preference,
    prepaid_packages,
    sales_office,
    booking_no,
    group_notes,
    group_files,
  } = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  return (
    <div>
      <div className={`grid grid-cols-3 gap-4 items-start ${props.className}`}>
        <BubbleFormGroup label={party_name} {...fieldValidation('name')}>
          <BubbleInput
            type="text"
            block
            value={props.partyForm.name}
            onValue={val => props.changePartyForm('name', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={group_id} {...fieldValidation('groupId')}>
          <BubbleInput
            type="text"
            block
            value={props.partyForm.groupId}
            onValue={val => props.changePartyForm('groupId', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={group_type} {...fieldValidation('groupType')}>
          <BubbleSelect
            block
            allowUndefined
            options={brand.groupTypes}
            labelExtractor={gt => gt.name}
            value={props.partyForm.groupType}
            onValue={val => props.changePartyForm('groupType', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup
          label={group_contact_name}
          className="col-span-2"
          {...fieldValidation('contact')}
        >
          <BubbleInput
            type="text"
            block
            value={props.partyForm.contact}
            onValue={val => props.changePartyForm('contact', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={`${guests} (min. 0)`} {...fieldValidation('size')}>
          <BubbleInput
            type="number"
            block
            min={0}
            value={props.partyForm.size}
            onValue={val => props.changePartyForm('size', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={tour_leader} {...fieldValidation('groupGuideName')}>
          <BubbleInput
            type="text"
            block
            value={props.partyForm.groupGuideName}
            onValue={val => props.changePartyForm('groupGuideName', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={`${tour_leader_cabin}`} {...fieldValidation('groupGuideCabin')}>
          <BubbleInput
            type="text"
            block
            value={props.partyForm.groupGuideCabin}
            onValue={val => props.changePartyForm('groupGuideCabin', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={booking_no} {...fieldValidation('bookingNo')}>
          <BubbleInput
            type="text"
            block
            value={props.partyForm.bookingNo}
            onValue={val => props.changePartyForm('bookingNo', val)}
          />
        </BubbleFormGroup>
        <div className="col-span-3 grid grid-cols-2 gap-4 items-center">
          <BubbleFormGroup label={sales_office} {...fieldValidation('salesOffice')}>
            <BubbleInput
              type="text"
              block
              value={props.partyForm.salesOffice}
              onValue={val => props.changePartyForm('salesOffice', val)}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label={agency_contact_name} {...fieldValidation('agencyContactName')}>
            <BubbleInput
              type="text"
              block
              value={props.partyForm.agencyContactName}
              onValue={val => props.changePartyForm('agencyContactName', val)}
            />
          </BubbleFormGroup>
        </div>
        <div className="col-span-3 grid grid-cols-2 gap-4 items-center">
          <BubbleFormGroup label={sales_dept_email} {...fieldValidation('salesEmail')}>
            <BubbleInput
              type="text"
              block
              value={props.partyForm.salesEmail}
              onValue={val => props.changePartyForm('salesEmail', val)}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label={sales_dept_phone} {...fieldValidation('salesPhone')}>
            <BubbleInput
              type="text"
              block
              value={props.partyForm.salesPhone}
              onValue={val => props.changePartyForm('salesPhone', val)}
            />
          </BubbleFormGroup>
        </div>
        <div className="col-span-3 grid grid-cols-2 gap-4 items-start">
          <BubbleFormGroup label={embark_date} {...fieldValidation('embark')}>
            <BubbleInput
              type="date"
              block
              max="9999-12-31"
              value={props.partyForm.embark}
              onValue={val => props.changePartyForm('embark', val)}
              {...dateValidityHandlers('embark')}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label={debark_date} {...fieldValidation('debark')}>
            <BubbleInput
              type="date"
              block
              max="9999-12-31"
              value={props.partyForm.debark}
              onValue={val => props.changePartyForm('debark', val)}
              {...dateValidityHandlers('debark')}
            />
          </BubbleFormGroup>
        </div>
        <BubbleFormGroup
          label={cabin_drops}
          {...fieldValidation('cabinDrops')}
          className="col-span-3"
        >
          <BubbleInput
            type="text"
            block
            value={props.partyForm.cabinDrops}
            onValue={val => props.changePartyForm('cabinDrops', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup
          label={dining_preference}
          {...fieldValidation('diningPreference')}
          className="col-span-3"
        >
          <BubbleInput
            type="text"
            block
            value={props.partyForm.diningPreference}
            onValue={val => props.changePartyForm('diningPreference', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup
          label={prepaid_packages}
          {...fieldValidation('prepaidPackages')}
          className="col-span-3"
        >
          <BubbleInput
            type="text"
            block
            value={props.partyForm.prepaidPackages}
            onValue={val => props.changePartyForm('prepaidPackages', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup
          label={guest_experience}
          className="col-span-3"
          {...fieldValidation('guestExperience')}
        >
          <Editor
            value={props.partyForm.guestExperience}
            onValue={val => props.changePartyForm('guestExperience', val)}
            mode="text-paste"
          />
        </BubbleFormGroup>

        <BubbleFormGroup label={group_notes} {...fieldValidation('notes')} className="col-span-3">
          <Editor
            value={props.partyForm.notes}
            onValue={val => props.changePartyForm('notes', val)}
            mode="text-paste"
          />
        </BubbleFormGroup>
        <div className="col-span-3">
          <BubbleFileM
            label={group_files}
            files={props.partyForm.files}
            onUpdateFile={props.onUpdateFile}
            onUploadFile={props.onUploadFile}
            uploadRule={UPLOAD_RULES.groupFile}
            hintPosition="inside"
            onDeleteFile={props.onDeleteFile}
          />
        </div>
        <BubbleFormGroup className="flex items-center col-span-3">
          <BubbleInput
            type="checkbox"
            block
            checked={props.partyForm.hospitalityDesk}
            onValue={val => props.changePartyForm('hospitalityDesk', val)}
            className="mr-2"
            inputClassName="bg-white"
          />
          <span>{hospitality_desk}</span>
        </BubbleFormGroup>

        <BubbleCollapsibleSection
          title="Balance & Payment Status"
          variant="transparent"
          defaultOpen={false}
          className="col-span-3"
        >
          <div className="grid grid-cols-3 gap-4 items-start">
            <BubbleFormGroup
              label="Balance Due"
              className="col-span-1"
              {...fieldValidation('balanceDue')}
            >
              <BubbleInput
                block
                type="text"
                value={props.partyForm.balanceDue}
                onValue={val => props.changePartyForm('balanceDue', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup
              label="Payment Status"
              className="col-span-2"
              {...fieldValidation('paymentStatus')}
            >
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2">
                  <RadioButtonGroup
                    key={`payment-status-${props.partyForm.paymentStatus || 'empty'}`}
                    name="paymentStatus"
                    onChange={val => props.changePartyForm('paymentStatus', val)}
                    groupValue={props.partyForm.paymentStatus || ''}
                  >
                    {paymentStatusProps => (
                      <>
                        <div className="flex items-center">
                          <RadioButton label="Pending" value={'pending'} {...paymentStatusProps} />
                        </div>
                        <div className="flex items-center">
                          <RadioButton label="Full" value={'full'} {...paymentStatusProps} />
                        </div>
                        <div className="flex items-center">
                          <RadioButton
                            label="Partially Collected"
                            value={'partially-collected'}
                            {...paymentStatusProps}
                          />
                        </div>
                      </>
                    )}
                  </RadioButtonGroup>

                  <BubbleButton
                    variant="smoke"
                    size="small"
                    onClick={() => props.changePartyForm('paymentStatus', '')}
                    className="mt-2 self-start"
                  >
                    Clear Selection
                  </BubbleButton>
                </div>
              </div>
            </BubbleFormGroup>
          </div>
        </BubbleCollapsibleSection>
      </div>
    </div>
  );
}
